import React, { useState } from "react";
import { useConnectWallet } from "features/home/redux/connectWallet";
import { useFetchEntityConfig } from "features/metadesk/redux/hooks";
import { StyledWhiteContainer } from "./common.styles";
import { Box, Divider, Switch, Button, Alert } from "@mui/material";
import NotFound from "features/metadesk/components/NotFound.js";
import StatusLabel from "components/StatusLabel/StatusLabel";
import { Loading } from "features/common";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { apiUrl } from "features/configure";

const MODULES = [
  { key: "service", label: "Community" },
  { key: "notification", label: "Notification" },
  { key: "CDP", label: "CDP" },
];

const InternalSettings = () => {
  const { userData } = useConnectWallet();
  const { entityConfig, fetchEntityConfig } = useFetchEntityConfig();
  const [entityName] = useOutletContext();
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState(null);
  // 本地 state: 記錄哪些 module 要 enable
  const [toEnable, setToEnable] = useState({});

  if (!userData || !entityConfig) {
    return <Loading open={true} fullScreen={false} />;
  }

  const isAdmin = userData?.team?.role?.isAdmin;
  const isInternal = userData?.team?.internal;

  if (!isAdmin || !isInternal) {
    return <NotFound />;
  }

  // 取得目前 enabled 狀態
  const enabledModules = entityConfig?.modules || {};

  // 切換 toggle
  const handleToggle = (key) => {
    setToEnable((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // 儲存
  const handleSave = async () => {
    setPending(true);
    setResult(null);
    try {
      const modules = Object.keys(toEnable).filter((k) => toEnable[k]);
      const res = await axios.post(
        `${apiUrl}/api/internal/${entityName}/enableModules`,
        { modules }
      );
      setResult(res.data);
      if (res.data.enabled?.length) {
        setToEnable({});
        await fetchEntityConfig({ entityName });
      }
    } catch (err) {
      setResult({ error: err?.response?.data?.error || "Unknown error" });
    } finally {
      setPending(false);
    }
  };

  return (
    <StyledWhiteContainer>
      <div className="title fade-up">Internal Settings</div>
      <div className="description fade-up" style={{ marginBottom: 24 }}>
        Only admin & internal team can access this page. You can enable disabled
        modules below.
      </div>
      <Box className="fade-up-more" mt={2}>
        {MODULES.map((mod, idx) => {
          const isEnabled = !!enabledModules[mod.key];
          const isChecked = isEnabled || !!toEnable[mod.key];
          return (
            <Box
              key={mod.key}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              py={2}
              px={1}
              sx={{
                background: idx % 2 === 0 ? "#FAFAFA" : "white",
                borderRadius: 2,
              }}
            >
              <Box fontWeight={600} fontSize={16} color="#383538">
                {mod.label}
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Switch
                  checked={isChecked}
                  disabled={isEnabled || pending}
                  onChange={() => handleToggle(mod.key)}
                  color="secondary"
                />
                <StatusLabel
                  label={
                    isEnabled ? "Enabled" : isChecked ? "To Enable" : "Disabled"
                  }
                  labelColor={
                    isEnabled ? "#14C864" : isChecked ? "#FFA500" : "#FF3296"
                  }
                  content={
                    isEnabled
                      ? "This module is enabled."
                      : isChecked
                      ? "Will be enabled after save."
                      : "This module is disabled."
                  }
                />
              </Box>
            </Box>
          );
        })}
        <Divider sx={{ mt: 3, mb: 2 }} />
        <Button
          variant="contained"
          color="secondary"
          disabled={pending || !Object.keys(toEnable).some((k) => toEnable[k])}
          onClick={handleSave}
        >
          {pending ? "Saving..." : "Save"}
        </Button>
        {result && (
          <Box mt={2}>
            {result.success && (
              <Alert severity="success" sx={{ mb: 1 }}>
                Enable Success!
              </Alert>
            )}
            {result.skipped?.length > 0 && (
              <Alert severity="error">Enable Failed!</Alert>
            )}
            {result.error && <Alert severity="error">{result.error}</Alert>}
          </Box>
        )}
      </Box>
    </StyledWhiteContainer>
  );
};

export default InternalSettings;
